import { useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-final-form';
import { map } from 'lodash';

import TextField from 'common/forms/TextField';
import {
  validatePositiveInteger,
  validateRequired,
  validateValueWithRegex,
} from 'common/forms/formValidations';

const ManualShipmentItemsSection = ({ itemCount, onItemCountChange }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  const handleAddItem = useCallback(() => {
    onItemCountChange(itemCount + 1);
  }, [itemCount, onItemCountChange]);

  const handleRemoveItem = useCallback(
    (indexToRemove) => {
      const currentItems = values.items || [];
      for (let i = indexToRemove; i < itemCount - 1; i += 1) {
        const nextItem = currentItems[i + 1] || {};
        form.change(`items[${i}].description`, nextItem.description || '');
        form.change(`items[${i}].quantity`, nextItem.quantity || '');
        form.change(`items[${i}].skuOrReference`, nextItem.skuOrReference || '');
      }

      const lastIdx = itemCount - 1;
      form.change(`items[${lastIdx}].description`, undefined);
      form.change(`items[${lastIdx}].quantity`, undefined);
      form.change(`items[${lastIdx}].skuOrReference`, undefined);

      onItemCountChange(itemCount - 1);
    },
    [itemCount, onItemCountChange, form, values.items]
  );

  const indices = Array.from({ length: itemCount }, (_, i) => i);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          Items
        </Typography>
        <Button variant='outlined' size='small' startIcon={<AddIcon />} onClick={handleAddItem}>
          Add Item
        </Button>
      </Box>

      {itemCount === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
          No items added. Click &quot;Add Item&quot; to add at least one item.
        </Typography>
      )}

      {itemCount > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Description *</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Qty *</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {map(indices, (index) => (
              <TableRow key={index}>
                <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                  <TextField
                    name={`items[${index}].description`}
                    id={`ManualShipment-item-${index}-description`}
                    label=''
                    validations={[validateRequired()]}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 60, verticalAlign: 'top' }}>
                  <TextField
                    name={`items[${index}].quantity`}
                    id={`ManualShipment-item-${index}-quantity`}
                    label=''
                    validations={[validateRequired(), validatePositiveInteger()]}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 140, verticalAlign: 'top' }}>
                  <TextField
                    name={`items[${index}].skuOrReference`}
                    id={`ManualShipment-item-${index}-skuOrReference`}
                    label=''
                    validations={[
                      validateValueWithRegex('SKU must be 10 or 11 characters', /^.{10,11}$/),
                    ]}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <IconButton
                    size='small'
                    onClick={() => handleRemoveItem(index)}
                    aria-label={`Remove item ${index + 1}`}
                    disabled={itemCount <= 1}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Grid container justifyContent='flex-end' sx={{ mt: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </Typography>
      </Grid>
    </Box>
  );
};

ManualShipmentItemsSection.propTypes = {
  itemCount: PropTypes.number.isRequired,
  onItemCountChange: PropTypes.func.isRequired,
};

export default ManualShipmentItemsSection;
